import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { 
  ArrowLeft, 
  ArrowRight, 
  CalendarIcon, 
  MessageCircle, 
  CheckCircle,
  Upload,
  MapPin
} from "lucide-react";

const ClienteFormulario = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [datosPago, setDatosPago] = useState(null);
  const [fechaEvento, setFechaEvento] = useState(null);
  
  const [formData, setFormData] = useState({
    // Datos personales
    nombre: "",
    telefono: "",
    whatsapp: "",
    correo: "",
    // Evento
    tipo_evento: "",
    nombre_festejado: "",
    fecha_evento: "",
    hora_inicio: "",
    hora_fin: "",
    ubicacion: "",
    google_maps: "",
    // Invitación
    tema: "",
    colorimetria: "",
    codigo_vestimenta: "",
    mensaje_invitacion: "",
    referencias: "",
    rsvp_whatsapp: "",
    // Planeación
    total_invitados: "",
    adultos: "",
    ninos: "",
    servicios_requeridos: [],
    menu_preferido: "",
    decoracion: "",
    entretenimiento: "",
    alergias: "",
    necesidades_especiales: "",
    // Decisión
    tipo_servicio: "evento_completo",
    notas: ""
  });

  useEffect(() => {
    const fetchDatosPago = async () => {
      try {
        const response = await axios.get(`${API}/config/datos-pago`);
        setDatosPago(response.data);
      } catch {
        toast.error("Error al cargar datos de pago");
      }
    };
    fetchDatosPago();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServicioToggle = (servicio) => {
    setFormData(prev => ({
      ...prev,
      servicios_requeridos: prev.servicios_requeridos.includes(servicio)
        ? prev.servicios_requeridos.filter(s => s !== servicio)
        : [...prev.servicios_requeridos, servicio]
    }));
  };

  const handleDateSelect = (date) => {
    setFechaEvento(date);
    handleInputChange("fecha_evento", format(date, "yyyy-MM-dd"));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        total_invitados: formData.total_invitados ? parseInt(formData.total_invitados) : null,
        adultos: formData.adultos ? parseInt(formData.adultos) : null,
        ninos: formData.ninos ? parseInt(formData.ninos) : null,
      };

      await axios.post(`${API}/clientes`, dataToSend);
      toast.success("¡Solicitud enviada correctamente!");
      setStep(5); // Success step
    } catch {
      toast.error("Error al enviar la solicitud. Por favor intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const tiposEvento = [
    "Boda", "XV Años", "Bautizo", "Primera Comunión", "Cumpleaños", 
    "Baby Shower", "Despedida de Soltera", "Aniversario", "Graduación", "Otro"
  ];

  const serviciosDisponibles = [
    "Catering", "Decoración", "Sonido", "Iluminación", "Mobiliario", 
    "Fotografía", "Video", "Música en vivo", "DJ", "Pastel", "Mesa de dulces"
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <span className="text-[#C89B7B] text-sm tracking-[0.2em] uppercase font-medium">Paso 1 de 4</span>
              <h2 className="font-display text-3xl text-stone-900 mt-2">Tus datos</h2>
              <p className="text-stone-600 mt-2">Información de contacto</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre completo *</Label>
                <Input 
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange("nombre", e.target.value)}
                  placeholder="Tu nombre"
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input 
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => handleInputChange("telefono", e.target.value)}
                  placeholder="10 dígitos"
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-telefono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp">WhatsApp *</Label>
                <Input 
                  id="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                  placeholder="Número de WhatsApp"
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-whatsapp"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="correo">Correo electrónico</Label>
                <Input 
                  id="correo"
                  type="email"
                  value={formData.correo}
                  onChange={(e) => handleInputChange("correo", e.target.value)}
                  placeholder="tu@email.com"
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-correo"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <span className="text-[#C89B7B] text-sm tracking-[0.2em] uppercase font-medium">Paso 2 de 4</span>
              <h2 className="font-display text-3xl text-stone-900 mt-2">Tu evento</h2>
              <p className="text-stone-600 mt-2">Detalles del evento</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Tipo de evento *</Label>
                <Select value={formData.tipo_evento} onValueChange={(v) => handleInputChange("tipo_evento", v)}>
                  <SelectTrigger className="border-stone-300" data-testid="select-tipo-evento">
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposEvento.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nombre_festejado">Nombre del festejado *</Label>
                <Input 
                  id="nombre_festejado"
                  value={formData.nombre_festejado}
                  onChange={(e) => handleInputChange("nombre_festejado", e.target.value)}
                  placeholder="¿Quién es el festejado?"
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-nombre-festejado"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha del evento *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-left font-normal border-stone-300"
                      data-testid="btn-fecha-evento"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {fechaEvento ? format(fechaEvento, "PPP", { locale: es }) : "Selecciona la fecha"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={fechaEvento}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hora_inicio">Hora inicio</Label>
                  <Input 
                    id="hora_inicio"
                    type="time"
                    value={formData.hora_inicio}
                    onChange={(e) => handleInputChange("hora_inicio", e.target.value)}
                    className="border-stone-300 focus:border-emerald-900"
                    data-testid="input-hora-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hora_fin">Hora fin</Label>
                  <Input 
                    id="hora_fin"
                    type="time"
                    value={formData.hora_fin}
                    onChange={(e) => handleInputChange("hora_fin", e.target.value)}
                    className="border-stone-300 focus:border-emerald-900"
                    data-testid="input-hora-fin"
                  />
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ubicacion">Ubicación del evento</Label>
                <Input 
                  id="ubicacion"
                  value={formData.ubicacion}
                  onChange={(e) => handleInputChange("ubicacion", e.target.value)}
                  placeholder="Dirección o nombre del lugar"
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-ubicacion"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="google_maps" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Enlace Google Maps
                </Label>
                <Input 
                  id="google_maps"
                  value={formData.google_maps}
                  onChange={(e) => handleInputChange("google_maps", e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-google-maps"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <span className="text-[#C89B7B] text-sm tracking-[0.2em] uppercase font-medium">Paso 3 de 4</span>
              <h2 className="font-display text-3xl text-stone-900 mt-2">Tu invitación</h2>
              <p className="text-stone-600 mt-2">Personaliza tu diseño</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="tema">Tema o estilo</Label>
                <Input 
                  id="tema"
                  value={formData.tema}
                  onChange={(e) => handleInputChange("tema", e.target.value)}
                  placeholder="Ej: Elegante, Rústico, Moderno..."
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-tema"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="colorimetria">Colorimetría</Label>
                <Input 
                  id="colorimetria"
                  value={formData.colorimetria}
                  onChange={(e) => handleInputChange("colorimetria", e.target.value)}
                  placeholder="Ej: Rosa y dorado, Azul marino..."
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-colorimetria"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codigo_vestimenta">Código de vestimenta</Label>
                <Input 
                  id="codigo_vestimenta"
                  value={formData.codigo_vestimenta}
                  onChange={(e) => handleInputChange("codigo_vestimenta", e.target.value)}
                  placeholder="Ej: Formal, Casual elegante..."
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-codigo-vestimenta"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rsvp_whatsapp">WhatsApp para RSVP</Label>
                <Input 
                  id="rsvp_whatsapp"
                  value={formData.rsvp_whatsapp}
                  onChange={(e) => handleInputChange("rsvp_whatsapp", e.target.value)}
                  placeholder="Número para confirmar asistencia"
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="input-rsvp-whatsapp"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="mensaje_invitacion">Mensaje para la invitación</Label>
                <Textarea 
                  id="mensaje_invitacion"
                  value={formData.mensaje_invitacion}
                  onChange={(e) => handleInputChange("mensaje_invitacion", e.target.value)}
                  placeholder="Texto que deseas incluir en la invitación..."
                  rows={4}
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="textarea-mensaje"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="referencias">Referencias o inspiración</Label>
                <Textarea 
                  id="referencias"
                  value={formData.referencias}
                  onChange={(e) => handleInputChange("referencias", e.target.value)}
                  placeholder="Describe o comparte links de diseños que te gusten..."
                  rows={3}
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="textarea-referencias"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <span className="text-[#C89B7B] text-sm tracking-[0.2em] uppercase font-medium">Paso 4 de 4</span>
              <h2 className="font-display text-3xl text-stone-900 mt-2">Servicios adicionales</h2>
              <p className="text-stone-600 mt-2">¿Necesitas más que una invitación?</p>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <Label>¿Qué servicio te interesa?</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { value: "evento_completo", label: "Evento completo", desc: "Organización integral del evento" },
                    { value: "invitacion_cotizacion", label: "Solo cotización", desc: "Recibe presupuesto para tu evento" },
                  ].map(option => (
                    <div
                      key={option.value}
                      onClick={() => handleInputChange("tipo_servicio", option.value)}
                      className={`card-premium cursor-pointer ${
                        formData.tipo_servicio === option.value 
                          ? 'border-emerald-900 bg-emerald-50' 
                          : ''
                      }`}
                      data-testid={`option-${option.value}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          formData.tipo_servicio === option.value 
                            ? 'border-emerald-900 bg-emerald-900' 
                            : 'border-stone-300'
                        }`}>
                          {formData.tipo_servicio === option.value && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{option.label}</p>
                          <p className="text-sm text-stone-500">{option.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {(
                <div className="space-y-6 pt-6 border-t border-stone-200 animate-fade-in">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="total_invitados">Total de invitados</Label>
                      <Input 
                        id="total_invitados"
                        type="number"
                        value={formData.total_invitados}
                        onChange={(e) => handleInputChange("total_invitados", e.target.value)}
                        className="border-stone-300 focus:border-emerald-900"
                        data-testid="input-total-invitados"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="adultos">Adultos</Label>
                      <Input 
                        id="adultos"
                        type="number"
                        value={formData.adultos}
                        onChange={(e) => handleInputChange("adultos", e.target.value)}
                        className="border-stone-300 focus:border-emerald-900"
                        data-testid="input-adultos"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ninos">Niños</Label>
                      <Input 
                        id="ninos"
                        type="number"
                        value={formData.ninos}
                        onChange={(e) => handleInputChange("ninos", e.target.value)}
                        className="border-stone-300 focus:border-emerald-900"
                        data-testid="input-ninos"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Servicios que te interesan</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {serviciosDisponibles.map(servicio => (
                        <div 
                          key={servicio}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox 
                            id={servicio}
                            checked={formData.servicios_requeridos.includes(servicio)}
                            onCheckedChange={() => handleServicioToggle(servicio)}
                            data-testid={`checkbox-${servicio.toLowerCase().replace(/ /g, '-')}`}
                          />
                          <label htmlFor={servicio} className="text-sm text-stone-700 cursor-pointer">
                            {servicio}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="alergias">Alergias alimentarias</Label>
                      <Input 
                        id="alergias"
                        value={formData.alergias}
                        onChange={(e) => handleInputChange("alergias", e.target.value)}
                        placeholder="Especifica alergias de los invitados"
                        className="border-stone-300 focus:border-emerald-900"
                        data-testid="input-alergias"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="necesidades_especiales">Necesidades especiales</Label>
                      <Input 
                        id="necesidades_especiales"
                        value={formData.necesidades_especiales}
                        onChange={(e) => handleInputChange("necesidades_especiales", e.target.value)}
                        placeholder="Accesibilidad, dietas especiales..."
                        className="border-stone-300 focus:border-emerald-900"
                        data-testid="input-necesidades"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notas">Notas adicionales</Label>
                <Textarea 
                  id="notas"
                  value={formData.notas}
                  onChange={(e) => handleInputChange("notas", e.target.value)}
                  placeholder="¿Algo más que debamos saber?"
                  rows={3}
                  className="border-stone-300 focus:border-emerald-900"
                  data-testid="textarea-notas"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="text-center space-y-6 animate-fade-in py-12">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-emerald-900" />
            </div>
            <h2 className="font-display text-3xl text-stone-900">¡Solicitud enviada!</h2>
            <p className="text-stone-600 max-w-md mx-auto">
              Hemos recibido tu información. Recuerda enviar tu comprobante de pago por WhatsApp para que podamos comenzar con tu diseño.
            </p>
            
            <div className="bg-stone-100 rounded-sm p-6 max-w-md mx-auto space-y-4">
              <p className="text-sm text-stone-500 uppercase tracking-wider">Siguiente paso</p>
              <p className="text-stone-700">Envía tu comprobante de transferencia por WhatsApp</p>
              <a 
                href={`https://wa.me/52${datosPago?.whatsapp || '9993388933'}?text=Hola,%20acabo%20de%20realizar%20mi%20pago%20para%20mi%20invitación.%20Adjunto%20comprobante.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent inline-flex items-center justify-center gap-2 w-full"
                data-testid="btn-enviar-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                Enviar por WhatsApp
              </a>
            </div>

            <Link 
              to="/"
              className="inline-flex items-center text-stone-600 hover:text-stone-900 transition-colors"
              data-testid="btn-volver-inicio"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Link>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.nombre && formData.telefono && formData.whatsapp;
      case 2:
        return formData.tipo_evento && formData.nombre_festejado && formData.fecha_evento;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight text-emerald-900">
            Celebra<span className="font-bold">Mid</span>
          </Link>
          {step < 5 && datosPago && (
            <div className="text-right">
              <p className="text-xs text-stone-500 uppercase tracking-wider">Precio</p>
              <p className="font-display text-xl text-emerald-900">${datosPago.costo} MXN</p>
            </div>
          )}
        </div>
      </header>

      {/* Progress Bar */}
      {step < 5 && (
        <div className="bg-white border-b border-stone-200">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-stone-600">Progreso</span>
              <span className="text-sm text-stone-600">{step} de 4</span>
            </div>
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-900 transition-all duration-500"
                style={{ width: `${(step / 4) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {renderStep()}

        {/* Navigation Buttons */}
        {step < 5 && (
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-stone-200">
            {step > 1 ? (
              <Button 
                variant="outline" 
                onClick={() => setStep(step - 1)}
                className="border-stone-300"
                data-testid="btn-anterior"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
            ) : (
              <Link to="/">
                <Button variant="outline" className="border-stone-300" data-testid="btn-cancelar">
                  Cancelar
                </Button>
              </Link>
            )}

            {step < 4 ? (
              <Button 
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="btn-primary"
                data-testid="btn-siguiente"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="btn-accent"
                data-testid="btn-enviar"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar solicitud
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </main>

      {/* Payment Info Sidebar */}
      {step < 5 && datosPago && (
        <div className="fixed bottom-0 left-0 right-0 md:bottom-6 md:left-auto md:right-6 md:w-80 bg-white border border-stone-200 rounded-t-lg md:rounded-lg shadow-lg z-40">
          <div className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider mb-3">Datos de pago</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-stone-500">Banco:</span>
                <span className="font-medium">{datosPago.banco}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Titular:</span>
                <span className="font-medium">{datosPago.titular}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">CLABE:</span>
                <span className="font-mono text-xs">{datosPago.clabe}</span>
              </div>
            </div>
            <a 
              href={`https://wa.me/52${datosPago.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full btn-primary text-sm flex items-center justify-center gap-2"
              data-testid="btn-whatsapp-sidebar"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp: {datosPago.whatsapp}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClienteFormulario;
